import { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { Brain } from 'lucide-react'
import { ThemeToggle } from '../components/ui/ThemeToggle'
import { LanguageToggle } from '../components/ui/LanguageToggle'
import { useLanguage } from '../contexts/LanguageContext'
import { supabase } from '../lib/supabase'
import SimpleTracking from '../components/beta/SimpleTracking'

export default function DashboardPage() {
  const { t } = useLanguage()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        setIsAuthenticated(!!user)
      } catch (error) {
        console.error('Auth check error:', error)
        setIsAuthenticated(false)
      } finally {
        setLoading(false)
      }
    }

    checkAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setIsAuthenticated(!!session?.user)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-night-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-slate-300">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  // Redirect to beta if not authenticated
  if (isAuthenticated === false) {
    return <Navigate to="/beta" replace />
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-night-950">
      {/* Header */}
      <header className="bg-white dark:bg-night-900/85 shadow-sm border-b border-gray-100 dark:border-night-850/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                <Brain className="text-white w-5 h-5" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-gray-900 dark:text-slate-100">{t('dashboard.title')}</h1>
                <p className="text-sm text-gray-500 dark:text-slate-400">{t('dashboard.subtitle')}</p>
              </div>
              <div className="sm:hidden">
                <h1 className="text-lg font-bold text-gray-900 dark:text-slate-100">{t('dashboard.titleShort')}</h1>
              </div>
            </a>
            <div className="flex items-center gap-2 sm:gap-4">
              <ThemeToggle />
              <LanguageToggle />
            </div>
          </div>
        </div>
      </header>

      {/* Main Dashboard Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <SimpleTracking />
      </main>

      {/* Footer */}
      <footer className="bg-zinc-900 dark:bg-night-900 text-white dark:text-slate-100 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold">{t('dashboard.title')}</span>
            </div>
            <p className="text-gray-400 dark:text-ocean-300/80 mb-4">
              {t('footer.tagline')}
            </p>
            <div className="flex flex-col items-center justify-center gap-2 text-sm text-gray-500 dark:text-ocean-200/80">
              <div className="flex items-center gap-4">
                <span>{t('footer.copyright.trainiq')}</span>
                <span>•</span>
                <span>{t('footer.powered.muaythai')}</span>
                <span>•</span>
                <span>{t('footer.version')}</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}